/**
 * BoardStage — the live demo board: sentence strip + the fixed 6×8 board
 * that mirrors the founder's son's real device. Everything on one screen,
 * no folders, tiles never move. Tap = speak + (for living tiles) a short
 * clip that plays once inside the tile and settles back to its poster.
 * Used inside the mock tablet on both the landing page and /demo.
 *
 * Living Board rules (Fable spec, 2026-07-12):
 *   - Motion is brief, one-shot, interruptible, and limited to the tapped tile.
 *   - Speech always starts immediately; animation never delays it.
 *   - Only one living tile plays at a time.
 *   - `Still` mode and prefers-reduced-motion skip motion — the poster frame
 *     is always the semantic fallback, so the child can always communicate.
 *   - Night mode changes presentation only: positions/vocabulary never move.
 *
 * Self-contained and offline: no server calls, nothing persists.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Volume2, Trash2, Play, Pause } from "lucide-react";
import { speak, cancelSpeech, isSupported as ttsSupported } from "@/lib/tts";
import Pictogram from "@/components/demoboard/Pictogram";
import { LIVING_BOARD_ROWS, DEFAULT_COLORS, BOARD_COLS, BOARD_ROWS } from "@/components/demoboard/vocabulary";
import "@/components/demoboard/demoboard.css";

const MAX_TOKENS = 10;
const GESTURE_MS = 1500;
const FRAME_HOLD_MS = 115;   // per Fable asset-manifest
const POSTER_RESET_MS = 140;

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Sprite-sheet cell → background-position for a clip tile.
function clipPosition(art, cell) {
  const col = cell % art.cols;
  const row = Math.floor(cell / art.cols);
  const x = art.cols > 1 ? (col / (art.cols - 1)) * 100 : 0;
  const y = art.rows > 1 ? (row / (art.rows - 1)) * 100 : 0;
  return `${x}% ${y}%`;
}

export function TileArt({ art, playing, clipCell }) {
  if (art.type === "anim") return <Pictogram name={art.name} playing={playing} />;
  if (art.type === "img") return <img src={art.src} alt="" loading="lazy" draggable={false} />;
  if (art.type === "pic") {
    // Full-photo tile — the picture fills the whole tile (cover).
    return (
      <span
        className="tile-pic"
        aria-hidden
        style={{ backgroundImage: `url(${art.src})` }}
      />
    );
  }
  if (art.type === "clip") {
    const cell = clipCell != null ? clipCell : art.poster;
    return (
      <span
        className="tile-clip"
        role="img"
        aria-label={art.scene || ""}
        style={{
          backgroundImage: `url(${art.sprite})`,
          backgroundSize: `${art.cols * 100}% ${art.rows * 100}%`,
          backgroundPosition: clipPosition(art, cell),
        }}
      />
    );
  }
  if (art.type === "photo") {
    return (
      <span className="tile-photoframe" aria-hidden>
        <svg className="tile-photocam" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
          <circle cx="12" cy="13" r="4" />
        </svg>
        <span className="tile-photohint">your photo</span>
      </span>
    );
  }
  return <span className="tile-emoji" aria-hidden>{art.char}</span>;
}

function TokenArt({ art }) {
  if (art.type === "anim") return <Pictogram name={art.name} />;
  if (art.type === "img") return <img src={art.src} alt="" draggable={false} />;
  if (art.type === "pic") {
    return <span className="token-clip" aria-hidden style={{ backgroundImage: `url(${art.src})`, backgroundSize: "cover" }} />;
  }
  if (art.type === "clip") {
    return (
      <span
        className="token-clip"
        aria-hidden
        style={{
          backgroundImage: `url(${art.sprite})`,
          backgroundSize: `${art.cols * 100}% ${art.rows * 100}%`,
          backgroundPosition: clipPosition(art, art.poster),
        }}
      />
    );
  }
  if (art.type === "photo") return <span className="token-emoji" aria-hidden>🖼️</span>;
  return <span className="token-emoji" aria-hidden>{art.char}</span>;
}

export default function BoardStage({ idPrefix = "demo", colors = DEFAULT_COLORS }) {
  const [sentence, setSentence] = useState([]);
  const [tappedId, setTappedId] = useState(null);
  const [playingId, setPlayingId] = useState(null); // anim (pictogram) gestures
  const [clip, setClip] = useState(null); // { tileId, cell } for the active sprite clip
  const [speaking, setSpeaking] = useState(false);
  const [motion, setMotion] = useState("live"); // live | still
  const timers = useRef([]);
  const clipTimers = useRef([]);
  const autoIdx = useRef(0);
  const lastTapAt = useRef(0);

  const rows = useMemo(() => LIVING_BOARD_ROWS, []);
  const livingTiles = useMemo(() => rows.flat().filter((t) => t.living), [rows]);

  useEffect(() => () => {
    timers.current.forEach(clearTimeout);
    clipTimers.current.forEach(clearTimeout);
    cancelSpeech();
  }, []);

  const stopClip = useCallback(() => {
    clipTimers.current.forEach(clearTimeout);
    clipTimers.current = [];
    setClip(null);
  }, []);

  const playClip = useCallback((tile) => {
    stopClip();
    if (motion !== "live" || prefersReducedMotion()) return; // poster stays
    const { seq } = tile.art;
    let i = 0;
    const advance = () => {
      setClip({ tileId: tile.id, cell: seq[i] });
      i += 1;
      if (i < seq.length) {
        clipTimers.current.push(setTimeout(advance, FRAME_HOLD_MS));
      } else {
        clipTimers.current.push(setTimeout(() => setClip(null), POSTER_RESET_MS));
      }
    };
    advance();
  }, [motion, stopClip]);

  const onTap = useCallback((tile) => {
    // Speech first — always, before any motion.
    if (ttsSupported()) {
      cancelSpeech();
      speak(tile.speak || tile.label, { rate: 0.95, pitch: 1.05 });
    }
    setTappedId(tile.id);
    if (tile.art.type === "anim") setPlayingId(tile.id);
    if (tile.art.type === "clip") playClip(tile);
    setSentence((s) => (s.length >= MAX_TOKENS ? s : [...s, tile]));
    const t1 = setTimeout(() => setTappedId(null), 500);
    const t2 = setTimeout(() => setPlayingId(null), GESTURE_MS);
    timers.current.push(t1, t2);
  }, [playClip]);

  const speakSentence = useCallback(() => {
    if (sentence.length === 0 || !ttsSupported()) return;
    cancelSpeech();
    setSpeaking(true);
    const text = sentence.map((w) => w.label).join(" ");
    speak(text, { rate: 0.95, pitch: 1.05, onEnd: () => setSpeaking(false) });
    const t = setTimeout(() => setSpeaking(false), 1000 + text.length * 90);
    timers.current.push(t);
  }, [sentence]);

  const clearSentence = useCallback(() => {
    cancelSpeech();
    setSpeaking(false);
    setSentence([]);
  }, []);

  // Auto-turn (attract loop): in Live mode the living tiles take turns coming
  // alive on their own, so the board is visibly "living" without a tap. Pauses
  // for a few seconds whenever someone actually taps, and is fully off in Still
  // mode or when the device prefers reduced motion.
  useEffect(() => {
    if (motion !== "live" || prefersReducedMotion() || livingTiles.length === 0) return undefined;
    const iv = setInterval(() => {
      if (typeof document !== "undefined" && document.hidden) return;
      if (Date.now() - lastTapAt.current < 5000) return; // let the user's own taps breathe
      const tile = livingTiles[autoIdx.current % livingTiles.length];
      autoIdx.current += 1;
      playClip(tile);
    }, 3000);
    return () => clearInterval(iv);
  }, [motion, livingTiles, playClip]);

  const rootStyle = {
    "--cat-feel": colors.feel,
    "--cat-need": colors.need,
    "--cat-want": colors.want,
    "--cat-fav": colors.fav,
    "--cat-people": colors.people,
    "--cat-core": colors.core,
    "--board-custom-bg": colors.bg,
  };

  return (
    <div className="demoboard-root" data-view="day" data-motion={motion} style={rootStyle}>
      {/* One slim line does everything: Live/Still · the built sentence ·
          Speak/Clear. The old fat controls row is gone, so the tiles get
          that height back. This strip stays thin — controls sit inline. */}
      <div className="demoboard-sentence" data-testid={`${idPrefix}-controls`}>
        <button
          type="button"
          className="strip-ctl"
          aria-pressed={motion === "still"}
          onClick={() => {
            const next = motion === "live" ? "still" : "live";
            setMotion(next);
            if (next === "still") stopClip();
          }}
          data-testid={`${idPrefix}-motion-toggle`}
          title="Live / Still motion"
        >
          {motion === "live" ? <Play size={13} aria-hidden /> : <Pause size={13} aria-hidden />}
          <span className="strip-ctl-label">{motion === "live" ? "Live" : "Still"}</span>
        </button>

        <div className="strip-tokens" data-testid={`${idPrefix}-sentence`}>
          {sentence.length === 0 ? (
            <span className="strip-placeholder">Tap tiles to build a sentence…</span>
          ) : (
            sentence.map((w, i) => (
              <span key={`${w.id}-${i}`} className="strip-token" data-testid={`${idPrefix}-sentence-token-${i}`}>
                <TokenArt art={w.art} /> {w.label}
              </span>
            ))
          )}
        </div>

        <button
          type="button"
          onClick={speakSentence}
          disabled={sentence.length === 0}
          className={`mbtn mbtn-primary demoboard-speak ${speaking ? "is-speaking" : ""}`}
          data-testid={`${idPrefix}-speak-sentence`}
        >
          <Volume2 size={16} /> <span className="hidden sm:inline">Speak</span>
        </button>
        <button
          type="button"
          onClick={clearSentence}
          disabled={sentence.length === 0}
          className="mbtn mbtn-icon"
          aria-label="Clear sentence"
          data-testid={`${idPrefix}-clear-sentence`}
        >
          <Trash2 size={15} />
        </button>
      </div>

      {/* Fixed 6×8 board — the real device, one screen, tiles never move. */}
      <main
        className="demoboard-stage"
        style={{
          gridTemplateColumns: `repeat(${BOARD_COLS}, 1fr)`,
          gridTemplateRows: `repeat(${BOARD_ROWS}, 1fr)`,
          "--board-rows": BOARD_ROWS,
        }}
        data-testid={`${idPrefix}-stage`}
      >
        {rows.flat().map((tile) => (
          <button
            key={tile.id}
            type="button"
            onClick={() => onTap(tile)}
            className={`demoboard-tile ${tile.art.type === "photo" ? "is-placeholder" : ""} ${tile.art.type === "pic" ? "is-pic" : ""} ${tile.living ? "is-living" : ""} ${tappedId === tile.id ? "just-tapped" : ""}`}
            style={{ "--cat": `var(--cat-${tile.cat})` }}
            data-testid={`${idPrefix}-tile-${tile.id}`}
            aria-label={tile.living ? `Say ${tile.label}. Plays a short moving clip.` : `Say ${tile.label}`}
          >
            <div className="tile-topstrip" style={{ background: `var(--cat-${tile.cat})` }} aria-hidden />
            <div className="tile-art">
              <TileArt
                art={tile.art}
                playing={playingId === tile.id}
                clipCell={clip && clip.tileId === tile.id ? clip.cell : undefined}
              />
              {tile.living && <span className="live-badge" aria-hidden>LIVE</span>}
            </div>
            <div className="tile-label">{tile.label}</div>
          </button>
        ))}
      </main>
    </div>
  );
}
